import { List } from "@refinedev/antd";
import { useQuery } from "@tanstack/react-query";
import { Alert, Button, Segmented, Select, Space, Table, Tag, Typography } from "antd";
import dayjs from "dayjs";
import { Link, useNavigate, useSearchParams } from "react-router";

import { api, ApiError } from "../api";
import { DATE_TIME_FORMAT, ERROR_PROJECTS } from "../labels";
import type { SystemError, SystemErrorPage } from "../types";

const STATUS_OPTIONS = [
  { value: "unresolved", label: "Çözülmeyen" },
  { value: "resolved", label: "Çözülen" },
  { value: "ignored", label: "Yok sayılan" },
];

const PROJECT_OPTIONS = [
  { value: "", label: "Tüm uygulamalar" },
  ...Object.entries(ERROR_PROJECTS).map(([value, label]) => ({ value, label })),
];

/**
 * /system-errors (permission `system.errors`): errors from web, the API and office that
 * GlitchTip collected, the most recent first. GlitchTip pages with cursors, so the list has
 * "Önceki" / "Sonraki" instead of page numbers.
 */
export function SystemErrorsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const status = searchParams.get("status") ?? "unresolved";
  const project = searchParams.get("project") ?? "";
  const cursor = searchParams.get("cursor") ?? "";

  const query = new URLSearchParams({ status });
  if (project) query.set("project", project);
  if (cursor) query.set("cursor", cursor);
  const { data, error, isLoading } = useQuery({
    queryKey: ["office", "system-errors", status, project, cursor],
    queryFn: () => api<SystemErrorPage>(`/api/office/system-errors?${query}`),
  });

  function show(next: { status?: string; project?: string; cursor?: string | null }) {
    const params = new URLSearchParams();
    const nextStatus = next.status ?? status;
    const nextProject = next.project ?? project;
    if (nextStatus !== "unresolved") params.set("status", nextStatus);
    if (nextProject) params.set("project", nextProject);
    if (next.cursor) params.set("cursor", next.cursor);
    setSearchParams(params);
  }

  return (
    <List title="Sistem hataları">
      <Space wrap style={{ marginBottom: 16 }}>
        <Segmented
          options={STATUS_OPTIONS}
          value={status}
          onChange={(value) => show({ status: String(value), cursor: null })}
        />
        <Select
          aria-label="Uygulama"
          options={PROJECT_OPTIONS}
          value={project}
          onChange={(value: string) => show({ project: value, cursor: null })}
          style={{ minWidth: 180 }}
        />
      </Space>
      {error ? (
        <Alert
          type={error instanceof ApiError && error.code === "ERROR_TRACKING_OFF" ? "info" : "error"}
          message={error.message}
          showIcon
        />
      ) : (
        <>
          <Table<SystemError>
            dataSource={data?.data}
            loading={isLoading}
            rowKey="id"
            pagination={false}
            scroll={{ x: "max-content" }}
            locale={{ emptyText: "Bu listede hata yok." }}
            onRow={(record) => ({
              onClick: () => navigate(`/system-errors/${record.id}`),
              style: { cursor: "pointer" },
            })}
          >
            <Table.Column<SystemError>
              title="Hata"
              key="title"
              render={(_, item) => (
                <Space direction="vertical" size={0} style={{ maxWidth: 560 }}>
                  <Link to={`/system-errors/${item.id}`}>
                    <Typography.Text strong ellipsis style={{ maxWidth: 560 }}>
                      {item.title}
                    </Typography.Text>
                  </Link>
                  {item.culprit ? (
                    <Typography.Text type="secondary" ellipsis style={{ maxWidth: 560 }}>
                      {item.culprit}
                    </Typography.Text>
                  ) : null}
                </Space>
              )}
            />
            <Table.Column<SystemError>
              title="Uygulama"
              dataIndex="project"
              render={(value: string) => <Tag>{ERROR_PROJECTS[value] ?? value}</Tag>}
            />
            <Table.Column<SystemError> title="Sayı" dataIndex="count" align="right" />
            <Table.Column<SystemError>
              title="Son görülme"
              dataIndex="lastSeen"
              render={(value: string) => dayjs(value).format(DATE_TIME_FORMAT)}
            />
            <Table.Column<SystemError>
              title="İlk görülme"
              dataIndex="firstSeen"
              render={(value: string) => dayjs(value).format(DATE_TIME_FORMAT)}
            />
          </Table>
          <Space style={{ marginTop: 16, width: "100%", justifyContent: "space-between" }} wrap>
            <Typography.Text type="secondary">
              {data ? `Toplam ${data.total} hata` : ""}
            </Typography.Text>
            <Space>
              <Button
                disabled={!data?.previousCursor}
                onClick={() => show({ cursor: data?.previousCursor })}
              >
                Önceki
              </Button>
              <Button
                disabled={!data?.nextCursor}
                onClick={() => show({ cursor: data?.nextCursor })}
              >
                Sonraki
              </Button>
            </Space>
          </Space>
        </>
      )}
    </List>
  );
}
